import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Form, 
  Input, 
  Select, 
  Pagination, 
  Message, 
  Header, 
  Icon, 
  Label, 
  Modal, 
  Statistic,
  Grid,
  Segment,
  Divider,
  Tab
} from 'semantic-ui-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { API, showError, timestamp2string, isAdmin } from '../../helpers';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ExecutionBilling = () => {
  const [billings, setBillings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [searchExecutionId, setSearchExecutionId] = useState('');
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [summary, setSummary] = useState(null);

  const loadBillings = async (page = 1) => {
    setLoading(true);
    try {
      const url = `/api/admin/executions/billing?page=${page}&page_size=${pageSize}`;
      const res = await API.get(url);
      const { success, data, message } = res.data;
      if (success) {
        setBillings(data.billings || []);
        setTotal(data.total || 0);
        setActivePage(page);
      } else {
        showError(message);
      }
    } catch (error) {
      showError('获取计费数据失败');
    }
    setLoading(false);
  };

  const loadSummary = async () => {
    try {
      const res = await API.get('/api/admin/executions/billing/summary');
      const { success, data, message } = res.data;
      if (success) {
        setSummary(data);
      } else {
        showError(message);
      }
    } catch (error) {
      showError('获取计费统计失败');
    }
  };

  const loadBillingDetail = async (executionId) => {
    try {
      const res = await API.get(`/api/admin/executions/${executionId}/billing`);
      const { success, data, message } = res.data;
      if (success) {
        setSelectedBilling(data);
        setShowDetailModal(true);
      } else {
        showError(message);
      }
    } catch (error) {
      showError('获取计费详情失败');
    }
  };

  useEffect(() => {
    loadBillings();
    loadSummary();
  }, [pageSize]);

  const handlePageChange = (e, { activePage }) => {
    loadBillings(activePage);
  };

  const handleSearch = () => {
    if (searchExecutionId.trim()) {
      loadBillingDetail(searchExecutionId.trim());
    } else {
      loadBillings(1);
    }
  };

  const formatCost = (cost) => {
    return `$${cost.toFixed(4)}`;
  };

  const formatTokens = (tokens) => {
    return tokens.toLocaleString();
  };

  const renderSummaryCards = () => {
    if (!summary) return null;

    return (
      <Grid columns={4} stackable>
        <Grid.Column>
          <Statistic color="blue">
            <Statistic.Value>{summary.total_executions}</Statistic.Value>
            <Statistic.Label>总执行数</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="green">
            <Statistic.Value>{summary.executions_with_billing}</Statistic.Value>
            <Statistic.Label>有计费记录</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="orange">
            <Statistic.Value>{formatCost(summary.total_cost)}</Statistic.Value>
            <Statistic.Label>总成本</Statistic.Label>
          </Statistic>
        </Grid.Column>
        <Grid.Column>
          <Statistic color="purple">
            <Statistic.Value>{formatTokens(summary.total_tokens)}</Statistic.Value>
            <Statistic.Label>总Token数</Statistic.Label>
          </Statistic>
        </Grid.Column>
      </Grid>
    );
  };

  const renderTopModelsChart = () => {
    if (!summary || !summary.top_models) return null;

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={summary.top_models}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="model_name" />
          <YAxis />
          <Tooltip formatter={(value, name) => [formatCost(value), '成本']} />
          <Legend />
          <Bar dataKey="total_cost" fill="#8884d8" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderBillingTable = () => {
    return (
      <Table celled striped>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>执行ID</Table.HeaderCell>
            <Table.HeaderCell>用户ID</Table.HeaderCell>
            <Table.HeaderCell>总Token数</Table.HeaderCell>
            <Table.HeaderCell>总成本</Table.HeaderCell>
            <Table.HeaderCell>请求次数</Table.HeaderCell>
            <Table.HeaderCell>使用模型</Table.HeaderCell>
            <Table.HeaderCell>最后请求时间</Table.HeaderCell>
            <Table.HeaderCell>操作</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {billings.map((billing) => (
            <Table.Row key={billing.execution_id}>
              <Table.Cell>
                <code style={{ fontSize: '12px' }}>
                  {billing.execution_id.substring(0, 8)}...
                </code>
              </Table.Cell>
              <Table.Cell>{billing.user_id}</Table.Cell>
              <Table.Cell>{formatTokens(billing.total_tokens)}</Table.Cell>
              <Table.Cell>{formatCost(billing.total_cost)}</Table.Cell>
              <Table.Cell>{billing.request_count}</Table.Cell>
              <Table.Cell>
                <Label size="small">{billing.model_names}</Label>
              </Table.Cell>
              <Table.Cell>{timestamp2string(billing.last_request_time)}</Table.Cell>
              <Table.Cell>
                <Button 
                  size="small" 
                  onClick={() => loadBillingDetail(billing.execution_id)}
                >
                  详情
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  const renderDetailModal = () => {
    if (!selectedBilling) return null;

    const panes = [
      {
        menuItem: '执行信息',
        render: () => (
          <Tab.Pane>
            <Grid columns={2} stackable>
              <Grid.Column>
                <Segment>
                  <Header as="h4">基本信息</Header>
                  <p><strong>执行ID:</strong> <code>{selectedBilling.execution.id}</code></p>
                  <p><strong>用户ID:</strong> {selectedBilling.execution.user_id}</p>
                  <p><strong>设备ID:</strong> {selectedBilling.execution.device_id}</p>
                  <p><strong>应用ID:</strong> {selectedBilling.execution.app_id}</p>
                  <p><strong>仓库:</strong> {selectedBilling.execution.repo_name}</p>
                  <p><strong>脚本路径:</strong> {selectedBilling.execution.script_path}</p>
                </Segment>
              </Grid.Column>
              <Grid.Column>
                <Segment>
                  <Header as="h4">执行状态</Header>
                  <p><strong>状态:</strong> 
                    <Label color={selectedBilling.execution.status === 2 ? 'green' : 'red'}>
                      {selectedBilling.execution.status === 2 ? '成功' : '失败'}
                    </Label>
                  </p>
                  <p><strong>执行时长:</strong> {selectedBilling.execution.duration}ms</p>
                  <p><strong>开始时间:</strong> {selectedBilling.execution.start_time}</p>
                  <p><strong>创建时间:</strong> {timestamp2string(selectedBilling.execution.created_at)}</p>
                </Segment>
              </Grid.Column>
            </Grid>
          </Tab.Pane>
        )
      },
      {
        menuItem: '计费统计',
        render: () => (
          <Tab.Pane>
            <Grid columns={2} stackable>
              <Grid.Column>
                <Statistic.Group size="small">
                  <Statistic>
                    <Statistic.Value>{formatTokens(selectedBilling.summary.total_tokens)}</Statistic.Value>
                    <Statistic.Label>总Token数</Statistic.Label>
                  </Statistic>
                  <Statistic>
                    <Statistic.Value>{formatCost(selectedBilling.summary.total_cost)}</Statistic.Value>
                    <Statistic.Label>总成本</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Grid.Column>
              <Grid.Column>
                <Statistic.Group size="small">
                  <Statistic>
                    <Statistic.Value>{formatTokens(selectedBilling.summary.total_prompt_tokens)}</Statistic.Value>
                    <Statistic.Label>输入Token</Statistic.Label>
                  </Statistic>
                  <Statistic>
                    <Statistic.Value>{formatTokens(selectedBilling.summary.total_completion_tokens)}</Statistic.Value>
                    <Statistic.Label>输出Token</Statistic.Label>
                  </Statistic>
                </Statistic.Group>
              </Grid.Column>
            </Grid>
          </Tab.Pane>
        )
      },
      {
        menuItem: '详细记录',
        render: () => (
          <Tab.Pane>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>模型</Table.HeaderCell>
                  <Table.HeaderCell>输入Token</Table.HeaderCell>
                  <Table.HeaderCell>输出Token</Table.HeaderCell>
                  <Table.HeaderCell>总Token</Table.HeaderCell>
                  <Table.HeaderCell>成本</Table.HeaderCell>
                  <Table.HeaderCell>请求时间</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {selectedBilling.token_usages.map((usage, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{usage.model_name}</Table.Cell>
                    <Table.Cell>{formatTokens(usage.prompt_tokens)}</Table.Cell>
                    <Table.Cell>{formatTokens(usage.completion_tokens)}</Table.Cell>
                    <Table.Cell>{formatTokens(usage.total_tokens)}</Table.Cell>
                    <Table.Cell>{formatCost(usage.cost)}</Table.Cell>
                    <Table.Cell>{new Date(usage.request_time).toLocaleString()}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Tab.Pane>
        )
      }
    ];

    return (
      <Modal 
        open={showDetailModal} 
        onClose={() => setShowDetailModal(false)}
        size="large"
      >
        <Modal.Header>执行计费详情</Modal.Header>
        <Modal.Content>
          <Tab panes={panes} />
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowDetailModal(false)}>关闭</Button>
        </Modal.Actions>
      </Modal>
    );
  };

  if (!isAdmin()) {
    return (
      <Segment placeholder>
        <Header icon>
          <Icon name="lock" />
          权限不足
        </Header>
        <p>您需要管理员权限才能访问此页面。</p>
      </Segment>
    );
  }

  return (
    <div>
      <Header as="h2">
        <Icon name="dollar sign" />
        <Header.Content>
          执行计费管理
          <Header.Subheader>管理和查看执行记录的计费统计信息</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Header as="h3">计费统计概览</Header>
        {renderSummaryCards()}
      </Segment>

      {summary && summary.top_models && (
        <Segment>
          <Header as="h3">热门模型成本分析</Header>
          {renderTopModelsChart()}
        </Segment>
      )}

      <Segment>
        <Form>
          <Form.Group>
            <Form.Field width={12}>
              <Input
                placeholder="输入执行ID搜索..."
                value={searchExecutionId}
                onChange={(e) => setSearchExecutionId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Form.Field>
            <Form.Field width={2}>
              <Button primary onClick={handleSearch} loading={loading}>
                搜索
              </Button>
            </Form.Field>
            <Form.Field width={2}>
              <Button onClick={() => {
                setSearchExecutionId('');
                loadBillings(1);
              }}>
                重置
              </Button>
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field>
              <label>每页显示</label>
              <Select
                value={pageSize}
                onChange={(e, { value }) => setPageSize(value)}
                options={[
                  { key: 10, text: '10', value: 10 },
                  { key: 20, text: '20', value: 20 },
                  { key: 50, text: '50', value: 50 },
                  { key: 100, text: '100', value: 100 }
                ]}
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      <Card fluid>
        <Card.Content>
          <Card.Header>计费记录列表</Card.Header>
          <Card.Meta>共 {total} 条记录</Card.Meta>
        </Card.Content>
        <Card.Content>
          {loading ? (
            <Message>
              <Icon name="circle notched" loading />
              加载中...
            </Message>
          ) : billings.length === 0 ? (
            <Message warning>
              <Message.Header>暂无执行计费数据</Message.Header>
              <Message.Content>
                <p style={{ marginTop: '10px' }}>要记录执行计费数据，请确保：</p>
                <ol style={{ marginLeft: '20px', marginTop: '5px' }}>
                  <li>先调用 <code>/api/execution/start</code> 创建执行记录，获取 ExecutionID</li>
                  <li>在 LLM 调用时携带 <code>X-Execution-ID</code> Header</li>
                  <li>或在动态 API/动态 Prompt 调用时传递 <code>execution_id</code></li>
                </ol>
                <p style={{ marginTop: '10px', color: '#666' }}>
                  <Icon name="lightbulb outline" />
                  提示：执行计费功能用于统计一次脚本执行中所有 LLM 调用的 Token 消耗
                </p>
              </Message.Content>
            </Message>
          ) : (
            renderBillingTable()
          )}
        </Card.Content>
        {total > pageSize && (
          <Card.Content extra>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                activePage={activePage}
                totalPages={Math.ceil(total / pageSize)}
                onPageChange={handlePageChange}
                size="small"
              />
            </div>
          </Card.Content>
        )}
      </Card>

      {renderDetailModal()}
    </div>
  );
};

export default ExecutionBilling;
